import { useEffect, useState } from "react";
import clsx from "clsx";
import { PortalDialogProps } from "@app/services/modal.service";
import AuthService from "@app/services/http/auth.service";
import { addToast } from "@app/components/toast/toast.service";
import { useT } from "@app/contexts/preferences.context";
import useBusy from "@core/hooks/use-busy.hook";
import Modal from "../modal/modal.component";
import { IcMail, IcShield, IcX } from "../icons";
import { Spinner, btn, btnGhost, btnPrimary, iconBtn } from "../ui";
import { openSupport } from "../support-chat";

interface ChangePasswordModalProps {
  username: string;
  email: string;
}

const COOLDOWN = 60;

// Lúc gửi email gần nhất: giữ qua các lần mở modal để không gửi dồn (như bản thiết kế)
let lastSentAt = 0;

const secondsLeft = () => Math.max(0, COOLDOWN - Math.floor((Date.now() - lastSentAt) / 1000));

/**
 * Đổi mật khẩu theo giao diện mới: gửi liên kết đổi mật khẩu tới email của tài khoản.
 * Dùng đúng API cũ của trang /confirm-account (POST /auth/forget-password với username + email).
 */
function ChangePasswordModal({ portalDialogRef, portalData }: PortalDialogProps<ChangePasswordModalProps>) {
  const t = useT();
  const { busy, run } = useBusy();
  const username = portalData?.username || "";
  const email = portalData?.email || "";

  const [sent, setSent] = useState(secondsLeft() > 0);
  const [left, setLeft] = useState(secondsLeft());

  useEffect(() => {
    if (!left) return;

    const timer = setTimeout(() => setLeft(secondsLeft()), 1000);

    return () => clearTimeout(timer);
  }, [left]);

  const close = () => portalDialogRef.close();

  const send = () => {
    if (secondsLeft() > 0 || busy.send) return;

    run(
      "send",
      () => AuthService.forgetPassword({ username, email }),
      () => {
        lastSentAt = Date.now();
        setSent(true);
        setLeft(secondsLeft());
        addToast({ text: `${t("Đã gửi liên kết tới")} ${email}` });
      }
    );
  };

  const help = (e: React.MouseEvent) => {
    e.preventDefault();
    close();
    openSupport();
  };

  return (
    <Modal onCancel={close} buttonCancelInChildren={false}>
      <div className="flex w-[min(560px,calc(100vw-32px))] flex-col text-fg">
        <div className="flex items-start gap-3 border-b border-line px-[18px] pb-3 pt-4">
          <h2 className="flex-1 text-[17px] font-bold">{t("Đổi mật khẩu")}</h2>
          <button type="button" className={clsx(iconBtn, "!h-8 !w-8 flex-none")} aria-label={t("Đóng")} onClick={close}>
            <IcX className="h-4 w-4" />
          </button>
        </div>

        <div className="px-[18px] pb-[18px] pt-4">
          {sent ? (
            <div className="flex flex-col items-center gap-1.5 px-4 py-8 text-center">
              <div className="mb-2 grid h-14 w-14 place-items-center rounded-2xl bg-ok-soft text-ok">
                <IcMail className="h-[26px] w-[26px]" />
              </div>
              <h3 className="text-base font-bold">{t("Đã gửi liên kết đổi mật khẩu")}</h3>
              <p className="m-0 break-words text-muted [overflow-wrap:anywhere]">
                {t("Mở email")} <b className="font-mono text-[12.5px] text-fg">{email}</b>{" "}
                {t("và bấm vào liên kết để đặt mật khẩu mới. Không thấy email? Hãy xem mục Thư rác hoặc Quảng cáo.")}
              </p>
              <div className="mt-2.5 flex flex-wrap justify-center gap-2.5">
                <button type="button" className={clsx(btn, btnGhost)} disabled={left > 0 || busy.send} onClick={send}>
                  {busy.send && <Spinner />}
                  {left > 0 ? `${t("Gửi lại sau")} ${left} ${t("giây")}` : t("Gửi lại email")}
                </button>
                <button type="button" className={clsx(btn, btnPrimary)} onClick={close}>
                  {t("Xong")}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3.5">
              <p className="m-0 flex items-start gap-2.5 rounded-[10px] bg-brand-soft px-3 py-2.5 text-[13.5px]">
                <IcShield className="mt-px h-[18px] w-[18px] flex-none text-brand" />
                <span>
                  {t("Để bảo mật, PC Render gửi liên kết đổi mật khẩu tới email của tài khoản. Liên kết chỉ dùng được một lần.")}
                </span>
              </p>
              <div className="rounded-[10px] border border-line px-3.5">
                <div className="flex items-center justify-between gap-3 border-b border-line py-2.5">
                  <span className="text-[13px] text-muted">{t("Tên đăng nhập")}</span>
                  <b className="text-right font-mono text-[12.5px] [overflow-wrap:anywhere]">{username}</b>
                </div>
                <div className="flex items-center justify-between gap-3 py-2.5">
                  <span className="text-[13px] text-muted">{t("Gửi tới email")}</span>
                  <b className="text-right font-mono text-[12.5px] [overflow-wrap:anywhere]">{email}</b>
                </div>
              </div>
              <p className="m-0 text-[13px] text-muted">
                {t("Không còn dùng email này?")}{" "}
                <a href="#" className="font-semibold text-brand" onClick={help}>
                  {t("Liên hệ hỗ trợ")}
                </a>
                .
              </p>
              <div className="flex flex-wrap justify-end gap-2 max-[520px]:[&>button]:flex-1">
                <button type="button" className={clsx(btn, btnGhost)} onClick={close}>
                  {t("Huỷ")}
                </button>
                <button type="button" className={clsx(btn, btnPrimary)} disabled={busy.send || !email} onClick={send}>
                  {busy.send ? <Spinner /> : <IcMail className="h-[18px] w-[18px]" />}
                  {t("Gửi liên kết đổi mật khẩu")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default ChangePasswordModal;
